import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { Plant, Plus, Bell, BellSlash, Trash, Sparkle, MapPin, X, Crown, ArrowRight, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { getForecast } from "@/lib/weather";
import { nextStressDay, forecastDayLabel } from "@/lib/plantStress";
import { PlantLocationDialog } from "@/components/PlantLocationDialog";

const hasCoords = (p) => p.latitude != null && p.longitude != null;
const coordKey = (p) => `${p.latitude.toFixed(2)},${p.longitude.toFixed(2)}`;

export default function Plants() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ count: 0, limit: 1, is_pro: false });
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState({ common_name: "", scientific_name: "", nickname: "", location_label: "" });
  const [adding, setAdding] = useState(false);
  const [checking, setChecking] = useState(false);
  const [forecasts, setForecasts] = useState({}); // coordKey -> daily[] | "loading" | "error"
  const [locationPlant, setLocationPlant] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/plants");
      setItems(data.items);
      setMeta({ count: data.count, limit: data.limit, is_pro: data.is_pro });
    } catch {
      toast.error("Couldn't load plants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Per-plant forecasts keyed by rounded coords; plants in the same spot share one browser-side fetch.
  useEffect(() => {
    const keys = [...new Set(items.filter(hasCoords).map(coordKey))].filter((k) => !(k in forecasts));
    if (!keys.length) return;
    setForecasts((f) => ({ ...f, ...Object.fromEntries(keys.map((k) => [k, "loading"])) }));
    keys.forEach(async (k) => {
      const [lat, lon] = k.split(",").map(Number);
      let daily = [];
      try {
        daily = (await getForecast(lat, lon)).daily || [];
      } catch {
        // leave empty
      }
      setForecasts((f) => ({ ...f, [k]: daily.length ? daily : "error" }));
    });
  }, [items, forecasts]);

  const forecastFor = (p) => (hasCoords(p) ? forecasts[coordKey(p)] : null);

  const saveLocation = async (body) => {
    try {
      const { data } = await api.patch(`/plants/${locationPlant.plant_id}`, body);
      setItems((arr) => arr.map((x) => (x.plant_id === data.plant_id ? data : x)));
      toast.success(body.clear_location ? "Location removed" : "Location saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save location");
      throw e;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.common_name.trim()) return;
    setAdding(true);
    try {
      await api.post("/plants", form);
      toast.success("Plant added. Thresholds AI-generated.");
      setForm({ common_name: "", scientific_name: "", nickname: "", location_label: "" });
      setOpenAdd(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add plant");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this plant?")) return;
    try {
      await api.delete(`/plants/${id}`);
      setItems((arr) => arr.filter((p) => p.plant_id !== id));
      toast.success("Plant removed");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleAlerts = async (p) => {
    try {
      const { data } = await api.patch(`/plants/${p.plant_id}`, { alerts_enabled: !p.alerts_enabled });
      setItems((arr) => arr.map((x) => (x.plant_id === p.plant_id ? data : x)));
    } catch {
      toast.error("Couldn't update");
    }
  };

  const checkNow = () => {
    if (!meta.is_pro) {
      toast.error("Climate-stress alerts are a Grower feature.");
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    setChecking(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.post("/alerts/check", {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          if (data.alerts_created > 0) {
            toast.success(`${data.alerts_created} new alert(s). Check your bell.`);
          } else {
            toast.success(`No stress detected across your ${data.checked_plants} plant(s).`);
          }
        } catch (e) {
          toast.error(e.response?.data?.detail || "Couldn't run alert check");
        } finally {
          setChecking(false);
        }
      },
      () => {
        toast.error("Location permission denied");
        setChecking(false);
      }
    );
  };

  return (
    <div className="min-h-screen bg-sand-50" data-testid="plants-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-data text-mute">your collection</div>
            <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight">Plants</h1>
            <p className="text-mute mt-1">
              <span data-testid="plants-count">{meta.count}</span> / {meta.limit} {meta.limit === 1 ? "plant" : "plants"} {meta.is_pro ? "(Grower plan)" : "(Field plan)"}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={checkNow}
              disabled={checking || items.length === 0}
              className="btn-secondary !text-sm"
              data-testid="check-alerts-now"
            >
              <Bell size={14} /> {checking ? "Checking…" : "Check climate now"}
            </button>
            <button
              onClick={() => {
                if (meta.count >= meta.limit) {
                  toast.error(`You've reached the limit (${meta.limit}). Upgrade to add more.`);
                } else {
                  setOpenAdd(true);
                }
              }}
              className="btn-primary"
              data-testid="add-plant-btn"
            >
              <Plus size={14} weight="bold" /> Add plant
            </button>
          </div>
        </div>

        {!meta.is_pro && (
          <div className="mt-6 bg-ink text-white rounded-2xl p-5 flex items-start gap-4" data-testid="upgrade-banner">
            <div className="w-12 h-12 rounded-2xl bg-ochre flex items-center justify-center text-ink">
              <Crown size={22} weight="duotone" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-2xl">Unlock climate-stress alerts</h3>
              <p className="text-white/75 text-sm mt-1">
                Grower watches local weather for {meta.limit < 5 ? "your plants" : "you"} and pings you when temperature, humidity, UV, or rain crosses each plant's threshold.
              </p>
            </div>
            <Link to="/pricing" className="btn-primary !bg-ochre !text-ink hover:!bg-ochre-dark hover:!text-white whitespace-nowrap" data-testid="banner-upgrade-cta">
              Upgrade — $4.99
            </Link>
          </div>
        )}

        <div className="mt-8">
          {loading ? (
            <div className="font-mono-data text-mute animate-pulse-soft">loading…</div>
          ) : items.length === 0 ? (
            <div className="bg-sand-100 border border-sand-200 rounded-3xl p-12 text-center" data-testid="plants-empty">
              <Plant size={48} weight="duotone" className="text-forest mx-auto mb-4" />
              <h3 className="font-display text-3xl">Your collection is empty</h3>
              <p className="text-mute mt-2">Add a plant — we'll auto-derive its climate thresholds with GPT-5.5 and watch over it for you.</p>
              <button onClick={() => setOpenAdd(true)} className="btn-primary mt-6 inline-flex" data-testid="empty-add-plant"><Plus size={14} /> Add your first plant</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="plants-grid">
              {items.map((p) => (
                <PlantCard
                  key={p.plant_id}
                  p={p}
                  forecast={forecastFor(p)}
                  onSetLocation={() => setLocationPlant(p)}
                  onRemove={() => remove(p.plant_id)}
                  onToggle={() => toggleAlerts(p)}
                />
              ))}
            </div>
          )}
        </div>

        {locationPlant && (
          <PlantLocationDialog plant={locationPlant} onClose={() => setLocationPlant(null)} onSave={saveLocation} />
        )}

        {openAdd && (
          <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenAdd(false)} data-testid="add-plant-modal">
            <form onClick={(e) => e.stopPropagation()} onSubmit={add} className="bg-sand-50 rounded-3xl p-6 max-w-md w-full border border-sand-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-3xl">Add a plant</h2>
                <button type="button" onClick={() => setOpenAdd(false)} className="text-mute hover:text-ink"><X size={20} /></button>
              </div>
              <p className="text-sm text-mute mb-4">GPT-5.5 will auto-derive safe climate ranges for this plant.</p>
              <div className="space-y-4">
                <div>
                  <label className="font-mono-data text-mute">Common name *</label>
                  <input
                    required
                    value={form.common_name}
                    onChange={(e) => setForm({ ...form, common_name: e.target.value })}
                    placeholder="Tomato, Monstera, Basil…"
                    data-testid="plant-common-name"
                    className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                  />
                </div>
                <div>
                  <label className="font-mono-data text-mute">Scientific name (optional)</label>
                  <input
                    value={form.scientific_name}
                    onChange={(e) => setForm({ ...form, scientific_name: e.target.value })}
                    placeholder="Solanum lycopersicum"
                    data-testid="plant-scientific-name"
                    className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                  />
                </div>
                <div>
                  <label className="font-mono-data text-mute">Nickname (optional)</label>
                  <input
                    value={form.nickname}
                    onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                    placeholder="Patio Tom"
                    data-testid="plant-nickname"
                    className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                  />
                </div>
                <div>
                  <label className="font-mono-data text-mute">Location label (optional)</label>
                  <input
                    value={form.location_label}
                    onChange={(e) => setForm({ ...form, location_label: e.target.value })}
                    placeholder="South balcony"
                    data-testid="plant-location-label"
                    className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                  />
                </div>
              </div>
              <button type="submit" disabled={adding} className="btn-primary w-full justify-center mt-6" data-testid="submit-plant">
                <Sparkle size={14} weight="fill" /> {adding ? "Generating thresholds…" : "Add plant"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}

function PlantCard({ p, forecast, onSetLocation, onRemove, onToggle }) {
  const t = p.thresholds || {};
  const nextRisk = Array.isArray(forecast) ? nextStressDay(forecast, t) : null;
  return (
    <div className="bg-sand-100 border border-sand-200 rounded-2xl overflow-hidden" data-testid="plant-card">
      <Link to={`/plants/${p.plant_id}`} className="block group">
        <div className="aspect-[4/3] relative bg-forest/10 flex items-center justify-center">
          {p.thumbnail_b64 ? (
            <img src={`data:image/jpeg;base64,${p.thumbnail_b64}`} alt={p.common_name} className="w-full h-full object-cover" />
          ) : (
            <Plant size={56} weight="duotone" className="text-forest/60" />
          )}
          <span className="absolute top-3 left-3 tag">{t.category || "other"}</span>
          {nextRisk && (
            <span
              className="absolute top-3 right-3 bg-terra text-white text-[11px] font-mono-data uppercase tracking-wider px-2 py-1 rounded-full inline-flex items-center gap-1 shadow"
              data-testid="plant-card-risk-badge"
              title={nextRisk.breaches.join(" · ")}
            >
              <Warning size={11} weight="duotone" /> {forecastDayLabel(nextRisk.date, nextRisk.offsetDays)}
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display text-2xl text-ink truncate">{p.nickname || p.common_name}</h3>
            <ArrowRight size={14} weight="bold" className="text-forest opacity-0 group-hover:opacity-100 transition" />
          </div>
          <div className="text-mute text-sm italic">{p.scientific_name || p.common_name}</div>
          {(p.location_label || p.place_label) && (
            <div className="font-mono-data text-mute text-xs mt-2 flex items-center gap-1 min-w-0" data-testid="plant-card-location">
              <MapPin size={12} weight="duotone" className="shrink-0" />
              <span className="truncate">{[p.location_label, p.place_label?.split(",")[0]].filter(Boolean).join(" · ")}</span>
            </div>
          )}
          <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
            <Threshold label="temp" value={`${t.temp_c_min}–${t.temp_c_max}°C`} />
            <Threshold label="rh" value={`${t.humidity_pct_min}–${t.humidity_pct_max}%`} />
            <Threshold label="uv max" value={t.uv_index_max} />
          </div>
          <PlantRisk p={p} forecast={forecast} nextRisk={nextRisk} />
        </div>
      </Link>
      <div className="px-5 pb-5 flex items-center justify-between">
        <button
          onClick={(e) => { e.preventDefault(); onToggle(); }}
          className={`text-xs inline-flex items-center gap-1 transition ${p.alerts_enabled ? "text-forest" : "text-mute hover:text-forest"}`}
          data-testid="toggle-alerts"
        >
          {p.alerts_enabled ? <Bell size={14} weight="duotone" /> : <BellSlash size={14} weight="duotone" />}
          {p.alerts_enabled ? "alerts ON" : "alerts off"}
        </button>
        <button
          onClick={onSetLocation}
          className="text-xs inline-flex items-center gap-1 text-mute hover:text-forest transition-colors"
          data-testid="plant-set-location-btn"
        >
          <MapPin size={14} weight="duotone" /> {hasCoords(p) ? "change spot" : "set location"}
        </button>
        <button onClick={onRemove} className="text-mute hover:text-terra text-xs inline-flex items-center gap-1" data-testid="plant-delete">
          <Trash size={12} /> remove
        </button>
      </div>
    </div>
  );
}

function PlantRisk({ p, forecast, nextRisk }) {
  if (!hasCoords(p)) {
    return (
      <div className="mt-3 text-xs text-mute inline-flex items-center gap-1 font-mono-data" data-testid="plant-card-risk-no-location">
        <MapPin size={12} weight="duotone" /> Set a location to see forecast risk
      </div>
    );
  }
  if (forecast === "loading" || forecast === undefined) {
    return <div className="mt-3 text-xs text-mute font-mono-data animate-pulse-soft" data-testid="plant-card-risk-loading">checking forecast…</div>;
  }
  if (forecast === "error") {
    return <div className="mt-3 text-xs text-mute font-mono-data" data-testid="plant-card-risk-error">Forecast unavailable right now</div>;
  }
  if (!nextRisk) {
    return (
      <div className="mt-3 text-xs text-forest inline-flex items-center gap-1 font-mono-data" data-testid="plant-card-risk-clear">
        <CheckCircle size={12} weight="duotone" /> 7-day forecast looks safe
      </div>
    );
  }
  return (
    <div className="mt-3 bg-terra/5 border border-terra/30 rounded-xl p-2.5 flex items-start gap-2" data-testid="plant-card-risk-detail">
      <Warning size={14} weight="duotone" className="text-terra mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-mono-data text-[11px] text-terra uppercase tracking-wider">
          {forecastDayLabel(nextRisk.date, nextRisk.offsetDays)}
          {p.place_label ? <span className="text-mute normal-case ml-1">· {p.place_label.split(",")[0]}</span> : null}
        </div>
        <div className="text-xs text-ink mt-0.5 truncate">{nextRisk.breaches.join(" · ")}</div>
      </div>
    </div>
  );
}

function Threshold({ label, value }) {
  return (
    <div className="bg-white/70 border border-sand-200 rounded-lg p-2">
      <div className="font-mono-data text-mute">{label}</div>
      <div className="text-ink text-sm">{value ?? "—"}</div>
    </div>
  );
}
